import React from 'react';
import { Box, ButtonBase, Typography, Link } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import { SectionLabel } from './YouPrimitives';
import { isLiveNow, isUnread, postMeta } from '../lib/postDates';

const CARD_BG = '#141416';
const SOFT_BG = '#1c1c1f';
const RED = '#e5484d';
const TEXT_MUTED = 'rgba(255,255,255,0.55)';

function Banner({ post }) {
  const base = {
    position: 'relative',
    height: 84,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (post.image_url) {
    return (
      <Box
        data-testid="post-banner-image"
        sx={{
          ...base,
          backgroundImage: `url(${post.image_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    );
  }

  if (post.type === 'live') {
    return (
      <Box sx={{ ...base, background: 'linear-gradient(135deg, #1c1c1f, #2a2f3d)' }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PlayArrowRoundedIcon sx={{ fontSize: 20, color: '#fff' }} />
        </Box>
      </Box>
    );
  }

  if (post.type === 'release') {
    return (
      <Box sx={{ ...base, backgroundColor: SOFT_BG }}>
        {post.version ? (
          <Typography sx={{ fontSize: 30, fontWeight: 300, color: 'var(--accent)' }}>
            {post.version}
          </Typography>
        ) : (
          <TipsAndUpdatesOutlinedIcon sx={{ fontSize: 26, color: 'var(--accent)' }} />
        )}
      </Box>
    );
  }

  return <Box sx={{ ...base, background: 'linear-gradient(135deg, #1c1c1f, #26221c)' }} />;
}

export function PostCard({ post, seenBefore, onOpen }) {
  const live = isLiveNow(post);
  const unread = isUnread(post, seenBefore);

  // Le tap ouvre la news, jamais le lien externe : le carrousel n'affiche que le
  // titre, le corps n'est lisible que dans la liste. `action_url` reste un bouton,
  // celui de NewsList, comme le dit le schema (action_label = libelle du bouton).
  const handleClick = () => {
    if (onOpen) onOpen(post.id);
  };

  return (
    <ButtonBase
      onClick={handleClick}
      sx={{
        flex: '0 0 236px',
        scrollSnapAlign: 'start',
        display: 'block',
        width: 236,
        textAlign: 'left',
        backgroundColor: CARD_BG,
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Banner post={post} />

        {live && (
          <Typography
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              fontSize: 10,
              fontWeight: 700,
              color: '#fff',
              backgroundColor: RED,
              borderRadius: '20px',
              padding: '2px 7px',
              lineHeight: 1.4,
            }}
          >
            LIVE
          </Typography>
        )}

        {unread && (
          <Box
            data-testid="post-unread"
            aria-hidden
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: CARD_BG,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: RED }} />
          </Box>
        )}
      </Box>

      <Box sx={{ padding: '10px 12px 12px' }}>
        <Typography sx={{ fontSize: 11, color: TEXT_MUTED }}>{postMeta(post)}</Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: '#fff',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.title}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

export default function WhatsNewSection({ feed = [], unreadCount = 0, seenBefore, onAllNews }) {
  if (!feed.length) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <SectionLabel
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {unreadCount > 0 && (
              <Typography sx={{ fontSize: 12, color: 'var(--accent)' }}>
                {`${unreadCount} unread`}
              </Typography>
            )}
            <Link
              component="button"
              underline="hover"
              onClick={() => onAllNews()}
              sx={{ fontSize: 13, color: 'var(--accent)' }}
            >
              All news
            </Link>
          </Box>
        }
      >
        What's new
      </SectionLabel>

      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          overflowX: 'auto',
          margin: '0 -16px',
          padding: '0 16px',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollSnapType: 'x mandatory',
        }}
      >
        {feed.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            seenBefore={seenBefore}
            onOpen={(id) => onAllNews(id)}
          />
        ))}
      </Box>
    </Box>
  );
}
